import React, { Fragment, useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Row, Col, Radio } from '../../common/UIComponents'
import { alertMessage, base64ToPdf,  isEmpty } from '../../common/Common'
import I18n from '../../common/I18n'
import userStore from '../../stores/UserStore'
import { downloadLabels } from '../../api/OrdersApi'
import { fetchUnitLabelsForOrders } from '../../api/OrderItemUnits'
import { LableSettingApi } from '../../api/LableSettingApi'
import { checkServiceExistance } from '../../helpers/common'
import { OrgContext } from '../../context/OrgContext'
import BaseModal from '../BaseModal'
import Gallery from '../common/Gallery'
import { DownloadIcon, PrintIcon } from '../../common/CustomIcon'
import { base64ToBlob, printFile } from '../../common/preview'

const PrintLabels = ({
  handleSuccess,
  buttonType,
  className,
  label,
  isDisabled,
  orderIds,
  size,
  buttonIcon = "",
  isFromBulkContainer,
  isPrintLabelClicked,
  setIsPrintLabelClicked,
  setIsHovered, 
  fromContextMenu = false,
  handleIconClick = () => {},
}) => {
  const [loading, setLoading] = useState(false)
  const [printLoading, setPrintLoading] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [showInfoWindow, setShowInfoWindow] = useState(false)
  const [labelTypes, setLabelTypes] = useState([])
  const [selectedType, setSelectedType] = useState('')
  const [lightboxIsOpen, setLightboxIsOpen] = useState(false)
  const [pictures, setPictures] = useState([])
  const [currentImage, setCurrentImage] = useState(-1)
  // Items & Measure "Print Labels" (scannable-unit labels) preview state.
  const [unitLabelsLoading, setUnitLabelsLoading] = useState(false)
  const [showUnitPreview, setShowUnitPreview] = useState(false)
  const [unitPdfBase64, setUnitPdfBase64] = useState('')
  const [unitPdfUrl, setUnitPdfUrl] = useState('')
  const [unitFileName, setUnitFileName] = useState('')

  // When the org's "Print label based on LPN" config is on, "Print Labels"
  // skips the label-template picker entirely and prints the scannable-unit
  // (LPN) labels (server derives the units from the selected orders) with a
  // preview. Otherwise the old label-template popup flow is used.
  const { orgSettings } = useContext(OrgContext) || {}
  // LPN flow requires BOTH the Items & Measure screen to be enabled AND the
  // dedicated "print label based on LPN" config on. Otherwise keep the old
  // label-template popup. (print-by-LPN is a dependent config of IM enablement.)
  const unitLabelsMode =
    !!orgSettings &&
    orgSettings.items_measure_units_screen === 'true' &&
    orgSettings.print_label_based_on_lpn === 'true'

  useEffect(() => {
    if (isPrintLabelClicked) {
      enableForm();
    }
  }, [isPrintLabelClicked]);

  useEffect(() => {
    if(isFromBulkContainer && !showInfoWindow && !showUnitPreview){
      setIsPrintLabelClicked(false)
      setIsHovered(null);
    }
  }, [showInfoWindow, showUnitPreview]);

  const getLabelTypes = async () => {
    try {
      const organizationId = userStore.getStateValue('selectedOrg')
      const page = 1;
      const perPage = 1000;
      setLoading(true)
      let result = await LableSettingApi.fetch(organizationId, page, perPage);
      if (result.success) {
        const labels = [...result.data]
        setLabelTypes(labels)
        setSelectedType(labels.length > 0 ? labels[0].code : '')
        if (labels.length > 0) {
          setShowInfoWindow(true);
        } else {
          downloadOrPrintLabels('print');
        }
      } else {
        if (result.errors.length > 0) {
          downloadOrPrintLabels('print');
        }
      }
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }
  // useEffect(() => {
  //   // getLabelTypes()
  //   if (showInfoWindow && labelTypes.length === 0) {
  //     getLabelTypes();


  //   }
  // }, [showInfoWindow]);

  const enableForm = () => {
    handleIconClick();
    // Items & Measure: skip the template picker and go straight to the
    // scannable-unit labels (with a preview + Print/Download).
    if (unitLabelsMode) {
      generateUnitLabels();
      return;
    }
    getLabelTypes();
  }

  const generateUnitLabels = async () => {
    const selectedIds = !isEmpty(orderIds) ? orderIds : []
    if (isEmpty(selectedIds)) return
    try {
      setUnitLabelsLoading(true)
      const organizationId = userStore.getStateValue('selectedOrg')
      const result = await fetchUnitLabelsForOrders(selectedIds, organizationId)
      if (result.success && result.labels_pdf) {
        const blob = base64ToBlob(result.labels_pdf, 'application/pdf')
        const blobUrl = URL.createObjectURL(blob)
        setUnitPdfBase64(result.labels_pdf)
        setUnitPdfUrl(blobUrl)
        setUnitFileName(result.file_name || 'unit-labels.pdf')
        setShowUnitPreview(true)
      } else {
        const errs = result.errors || []
        alertMessage(errs.length > 0 ? errs.join(', ') : 'No labels to print for the selected orders', 'error', 10)
      }
    } catch (error) {
      alertMessage(error.message, 'error', 10)
    } finally {
      setUnitLabelsLoading(false)
    }
  }

  const printUnitLabels = () => {
    if (unitPdfUrl) {
      printFile(unitPdfUrl).catch((error) => alertMessage(error.message, 'error', 10))
    }
  }

  const downloadUnitLabels = () => {
    if (unitPdfBase64) base64ToPdf(unitPdfBase64, unitFileName)
  }

  const closeUnitPreview = () => {
    if (unitPdfUrl) URL.revokeObjectURL(unitPdfUrl)
    setShowUnitPreview(false)
    setUnitPdfUrl('')
    setUnitPdfBase64('')
    setUnitFileName('')
    handleSuccess()
  }

  const downloadOrPrintLabels = async (actionType = 'print') => {
    const selectedIds = !isEmpty(orderIds) ? orderIds : []
    try {
      const payload = {
        customer_order_ids: selectedIds,
        organization_id: userStore.getStateValue('selectedOrg'),
        label_type: selectedType,
      }
      if (actionType === 'download') {
        setDownloadLoading(true)
      } else {
        setPrintLoading(true)
      }
      let result = await downloadLabels(payload)
      if (result.success) {
        if (actionType === 'download') {
          base64ToPdf(result.label_file, `Labels.pdf`)
        } else {
          const blob = base64ToBlob(result.label_file, 'application/pdf')
          const blobUrl = URL.createObjectURL(blob)
          printFile(blobUrl).catch((error) => alertMessage(error.message, 'error', 10))
        }
        setShowInfoWindow(false)
        handleSuccess()
      } else {
        if (result.errors.length > 0) {
          alertMessage(result.errors.join(', '), 'error', 10)
        }
      }
    } catch (error) {
      alertMessage(error.message, 'error', 10)
    } finally {
      setPrintLoading(false)
      setDownloadLoading(false)
    }
  }

  const renderModalWindow = () => {
    const totalTypes = labelTypes.length > 0 ? labelTypes.length : 1
    const gridSize = 8 //totalTypes > 3 ? 8 :
    return (
      <BaseModal
        title={`${I18n.t('lable_settings.format_to_download')}`}
        onCancel={disableForm}
        width={'55%'}
        style={{ top: 40 }}
        bodyStyle={{ padding: 20 }}
        maskClosable={false}
      >
        <div data-testid="label-modal">
        <Radio.Group
          style={{ width: '100%' }}
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
        >
          <Row gutter={32} data-testid="label-select-option">
            {labelTypes.map((rec) => (
              <Col span={gridSize} className="textBold textCapitalize padding5">
                <Radio value={rec.code}>
                  {' '}
                  <span className="textCaptilize">
                    {rec.name ? rec.name.toLowerCase() : ''}{' '}
                  </span>
                </Radio>
                <br />
                <div
                  className="centerToBlock borderLine marginTop10 cursorPointer"
                  style={{
                    width: '100%',
                    height: 160,
                    border: '1px solid #000',
                  }}
                >
                  {rec.sample_label && rec.sample_label.url ? (
                    <img
                      src={rec.sample_label.url}
                      width={200}
                      height={150}
                      onClick={() => {
                        setSelectedType(rec.code)
                        // setPictures([
                        //   {
                        //     src: rec.sample_label.url,
                        //   },
                        // ])
                      }}
                    />
                  ) : (
                    <div
                      style={{ width: 200, height: 150, letterSpacing: '0.4pt' }}
                      onClick={() => {
                        setSelectedType(rec.code)
                      }}
                      className="centerToBlock fontSize18"
                    >
                      No Preview Available
                    </div>
                  )}
                  {/*  */}
                </div>

                {/* {
                rec.sample_label && rec.sample_label.url &&
                <img src={rec.sample_label.url} width={rec.width * 96} height={rec.height * 96} />
              } */}
              </Col>
            ))}
          </Row>
        </Radio.Group>
        <div className="alignCenter marginTop40">
          <Button
            data-testid="label-print-button"
            onClick={() => downloadOrPrintLabels('print')}
            type="primary"
            loading={printLoading}
            disabled={labelTypes.length === 0 || isEmpty(selectedType)}
            size={'default'}
            icon="printer"
            className="marginRight10"
          >
            Print
          </Button>
          <Button
            data-testid="label-download-button"
            onClick={() => downloadOrPrintLabels('download')}
            loading={downloadLoading}
            disabled={labelTypes.length === 0 || isEmpty(selectedType)}
            size={'default'}
            icon="download"
          >
            Download
          </Button>
        </div>
        </div>
      </BaseModal>
    )
  }

  const disableForm = () => {
    setSelectedType('')
    setShowInfoWindow(false)
  }

  const renderUnitPreview = () => (
    <BaseModal
      title={I18n.t('order.print_labels')}
      onCancel={closeUnitPreview}
      width={'70%'}
      style={{ top: 30 }}
      bodyStyle={{ padding: 12 }}
      maskClosable={false}
    >
      <div data-testid="unit-labels-preview">
        <iframe
          title="unit-labels-preview"
          src={unitPdfUrl}
          style={{ width: '100%', height: '65vh', border: '1px solid #d9d9d9' }}
        />
        <div className="alignCenter marginTop20">
          <Button
            data-testid="unit-labels-print-button"
            onClick={printUnitLabels}
            type="primary"
            icon="printer"
            className="marginRight10"
            size={'default'}
          >
            Print
          </Button>
          <Button
            data-testid="unit-labels-download-button"
            onClick={downloadUnitLabels}
            icon="download"
            size={'default'}
          >
            Download
          </Button>
        </div>
      </div>
    </BaseModal>
  )

  useEffect(() => {
    if (pictures.length > 0) {
      setCurrentImage(0)
      setLightboxIsOpen(true)
    } else {
      setCurrentImage(-1)
      setLightboxIsOpen(false)
    }
  }, [pictures])

  return (
    <Fragment>
      {fromContextMenu ? <Row onClick={enableForm} type="flex" gutter={12} align="middle">
        <Col >
          <PrintIcon />
        </Col>
        <Col className="context-menu-text">{label}</Col>
        </Row> : !isFromBulkContainer ? <Button
        onClick={enableForm}
        type={buttonType ? buttonType : 'primary'}
        icon="printer"
        className={className ? className : ''}
        loading={loading || printLoading || unitLabelsLoading}
        disabled={isDisabled}
        size={size ? size : 'default'}
      >
        {label}
      </Button> : I18n.t("general.label")}
      {showInfoWindow && renderModalWindow()}
      {showUnitPreview && unitPdfUrl && renderUnitPreview()}
      {lightboxIsOpen && pictures.length > 0 && (
        <Gallery
          images={pictures}
          onClose={() => {
            setPictures([])
          }}
          currentImage={currentImage}
        />
      )}
    </Fragment>
  )
}

PrintLabels.propTypes = {
  handleSuccess: PropTypes.func.isRequired,
  buttonType: PropTypes.string,
  className: PropTypes.string,
  label: PropTypes.string,
  isDisabled: PropTypes.bool.isRequired,
  orderIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  size: PropTypes.string.isRequired,
  isFromBulkContainer: PropTypes.bool,
  isPrintLabelClicked:  PropTypes.bool,
  setIsPrintLabelClicked: PropTypes.func,
  setIsHovered: PropTypes.func
}

PrintLabels.defaultProps = {
  handleSuccess: () => {},
  buttonType: PropTypes.bool.isRequired,
  className: '',
  label: I18n.t('general.download'),
  isDisabled: true,
  orderIds: [],
  size: 'default',
  isFromBulkContainer: false,
  isPrintLabelClicked: false,
  setIsPrintLabelClicked: () => {},
  setIsHovered: () => {}
}

export default PrintLabels
